import DateService from '@services/DateService'
import LayerStyles from '../mapping/common/LayerStyles'
import type { Variable as ServiceVariable } from './api/Variable'
import { Level, LevelReference } from './Level'
import type { Model, ModelID } from './Model'
import { RepresentationType } from './RepresentationType'
import { Dayjs } from 'dayjs'
import { Extent } from './api/Extent'
import i18n from 'src/i18n'
import { Minutes } from 'src/typings/time'

export enum VariableID {
  RADAR_ECHOTOP = 'RADAR_ECHOTOP',
  ACUMULADO_RADAR_1H = 'ACUMULADO_RADAR_1h',
  ACUMULADO_RADAR_6H = 'ACUMULADO_RADAR_6h',
  TEMPERATURE = 'T',
  PRECIPITATION = 'P',
  REL_HUMIDITY = 'RH',
  CONVECCION = 'CONVECCION',
  EUMETSAT = 'EUMETSAT',
  RADAR = 'RADAR',
  CATEGORIZED_RADAR = 'CATEGORIZED_RADAR',
  HUMIDITY = 'HR',
  CANADIENSE_WRF = 'CANADIENSE_WRF',
  ANOMALIAS_OBSERVACION = 'ANOMALIAS_OBSERVACION',
  TRIPLE_30 = 'CEF',
  SNOW = 'S',
  WIND = 'V',
  WIND_DIR = 'DV',
  WIND_ARROWS = 'WIND_ARROWS',
  WIND_DYN = 'WIND_DYN',
  WIND_30_D = 'OBS_W_30',
  ALUDES = 'Aludes',
  HOTSPOT = 'HOTSPOT',
  GEOPOT = 'G',
  WD = 'WD',
  FWI = 'FWI',
  PRESSURE = 'PRES',
  COVERAGE = 'C',
  EFFIS_EUROPEO = 'EFFIS_EUROPEO',
  EFFIS_METEOFRANCE = 'EFFIS_METEOFRANCE',
  FWI_AEMET = 'FWI_AEMET'
}

export namespace VariableID {
  export const getVariableName = (variable: VariableID) => {
    switch (variable) {
      case VariableID.TEMPERATURE:
        return i18n.t('variables.temperature')
      case VariableID.WIND:
        return i18n.t('variables.wind')
      case VariableID.REL_HUMIDITY:
        return i18n.t('variables.relativeHumidity')
      case VariableID.PRECIPITATION:
        return i18n.t('variables.precipitation')
      default:
        return variable
    }
  }
}

export class Variable {
  id: string
  reference: VariableID
  name: string
  date?: Dayjs
  outDate?: Dayjs
  representation: RepresentationType
  styleType: ServiceVariable['STYLE_TYPE']
  path: string
  url?: string
  colors: string[]
  intervals: number[]
  timeWindow: number
  timeInterval: Minutes
  timePrediction?: number
  steps: number
  stepsDelay?: number
  levels: Level[] = []
  style: L.StyleFunction
  idModel: ModelID
  lastIntervalDisable: boolean
  extent?: L.BoundsLiteral
  units: string
  outRangeMin: boolean
  outRangeMax: boolean
  icon?: string
  isLevelSelectorDisabled: boolean
  conversionFunction?: any
  visible: boolean
  tooltip: string
  isReducedModeCompatible: boolean
  groupId?: string

  static variablesNoPath = ['RADAR', 'RAYOS', 'DV']

  constructor(
    serviceVariable: ServiceVariable,
    {
      id: modelId,
      timeInterval: modelTimeInterval,
      timeWindow: modelTimeWindow,
      extent: modelExtent,
      groupId: modelGroupId
    }: Model,
    isNoLevelsAllowed: boolean
  ) {
    this.idModel = modelId
    this.groupId = modelGroupId
    this.reference = serviceVariable.VARIABLE as VariableID
    this.id = serviceVariable.ID_VARIABLE_INCENDIOS || this.reference
    this.name = serviceVariable.NAME || serviceVariable.VARIABLE
    this.date = DateService.utc(serviceVariable.DATE, 'YYYY-MM-DD')
    if (serviceVariable.SALIDA) {
      this.outDate = this.date?.hour(+serviceVariable.SALIDA)!
    }
    this.representation = serviceVariable.REPRESENTATION
    this.styleType = serviceVariable.STYLE_TYPE
    this.lastIntervalDisable = serviceVariable.LAST_INTERVAL_DISABLE
    this.path = serviceVariable.PATH
    this.url = serviceVariable.URL
    this.timeInterval = serviceVariable.TIME_INTERVAL ?? modelTimeInterval
    this.timeWindow = serviceVariable.TIME_WINDOW ?? modelTimeWindow
    this.timePrediction = serviceVariable.TIME_PREDICTION
    this.steps = serviceVariable.STEPS
    this.stepsDelay = serviceVariable.STEPS_DELAY
    this.intervals = serviceVariable.INTERVAL
    this.colors = serviceVariable.COLORS
    this.units = serviceVariable.UNIDADES
    this.visible = !serviceVariable.HIDE_SIDEBAR
    this.outRangeMin = serviceVariable.OUT_RANGE_MIN
    this.outRangeMax = serviceVariable.OUT_RANGE_MAX
    this.icon = serviceVariable.ICON
    this.isLevelSelectorDisabled = serviceVariable.DISABLE_LEVEL
    this.tooltip = serviceVariable.TOOLTIP
    const serviceConversionFunction = serviceVariable.CONVERSION_FUNCTION
      ? serviceVariable.CONVERSION_FUNCTION
      : null

    try {
      // eslint-disable-next-line no-new-func
      this.conversionFunction = serviceConversionFunction
        ? new Function(
            serviceConversionFunction.arguments,
            serviceConversionFunction.body
          )
        : undefined
    } catch (err) {
      console.error('Error parsing conversion function')
    }

    this.extent = Extent.toLeafletExtent(serviceVariable.EXTENT)
    if (!this.extent) {
      this.extent = modelExtent
    }

    if (this.isDiscreteIntervals()) {
      if (this.steps) {
        this.colors = LayerStyles.calculateColorScale(this.colors, this.steps)
      }
      this.style = LayerStyles.automaticStyleDiscreteValues(
        this.intervals,
        this.colors
      )
    } else if (this.isBinaryIntervals()) {
      this.style = LayerStyles.automaticStyleBinary()
    } else {
      if (serviceVariable.INTERVAL && serviceVariable.INTERVAL.length === 2) {
        const intervalColors = LayerStyles.calculateIntervalColors(
          this.intervals,
          this.colors,
          this.steps
        )
        this.colors = intervalColors.colorScale
        this.intervals = intervalColors.interval
      }
      this.style = LayerStyles.automaticStyle(
        this.intervals,
        this.colors,
        this.outRangeMin,
        this.outRangeMax
      )
    }

    if (serviceVariable.LEVELS) {
      if (this.isNoPathVariable()) {
        this.levels = serviceVariable.LEVELS.map(
          (level) => new Level(level, this)
        )
      } else {
        const levelsWithPath = serviceVariable.LEVELS.filter(
          (level) => level.PATH
        )
        this.levels = levelsWithPath?.map((level) => new Level(level, this))
      }
      if (this.levels.length < 1) {
        this.visible = false || isNoLevelsAllowed
      }
    }
    this.isReducedModeCompatible =
      Boolean(serviceVariable.SHOW_REDUCED_MODE) ||
      this.levels.some((level) => level.isReducedModeCompatible)

    this.orderLevels()
    // this.fixNoStartImagesOnSlider()
  }

  static fromFireModel(model: Model) {
    const variable = new Variable(
      {
        REPRESENTATION: model.variables![0].representation,
        VARIABLE: model.id,
        DATE: model.outDate.format('YYYY-MM-DD'),
        NAME: model.label,
        HIDE_SIDEBAR: !model.visible
      } as ServiceVariable,
      model,
      false
    )
    return variable
  }

  static fromObservationReport(variable: ServiceVariable) {
    const fakeModel = {
      id: 'X',
      timeInterval: 0,
      timeWindow: 0
    } as Model
    return new Variable(variable, fakeModel, true)
  }

  isDiscreteIntervals() {
    return this.styleType === 'discrete'
  }

  isBinaryIntervals() {
    return this.styleType === 'binary'
  }

  isRadar() {
    return (
      this.reference === VariableID.RADAR ||
      this.reference === VariableID.ACUMULADO_RADAR_1H ||
      this.reference === VariableID.ACUMULADO_RADAR_6H ||
      this.reference === VariableID.CATEGORIZED_RADAR ||
      this.reference === VariableID.RADAR_ECHOTOP
    )
  }

  isBackInTime() {
    return this.reference === VariableID.HOTSPOT
  }

  isNoPathVariable() {
    // return Variable.variablesNoPath.some((variable) => variable === this.id)
    return true
  }

  orderLevels() {
    if (this.reference === 'EUMETSAT' || this.reference === 'RADAR') {
      // TODO: ordernar alfabetico, hacer en backend
      this.levels.sort((a, b) => {
        return a.name.localeCompare(b.name)
      })
      const espLevel = this.levels.find(
        (level) => level.reference === LevelReference.ESP
      )
      if (espLevel) {
        this.levels.splice(this.levels.indexOf(espLevel), 1)
        this.levels.unshift(espLevel)
      }
    }
  }

  fixNoStartImagesOnSlider() {
    if (
      this.reference === VariableID.EUMETSAT ||
      this.reference === VariableID.CONVECCION
    ) {
      this.levels.forEach((x) => {
        x.timeWindow -= 2 * (this.timeInterval / 60)
      })
    } else if (this.reference === 'RADAR') {
      this.levels.forEach((x) => {
        x.timeWindow -= 3 * (this.timeInterval / 60)
      })
    }
  }

  getLevelByName(name: string) {
    return this.levels.find((level) => level.name === name)
  }

  getLevelByTransversalId(tId: string) {
    return this.levels.find((level) => level.transversalId === tId)
  }

  getHourlyDailyLevels() {
    return this.levels.filter((x) => x.isHourly() || x.isDaily())
  }

  // --- Para INCENDIOS ---
  getDate() {
    if (this.date && this.date.isValid()) {
      return this.date
    }
  }

  hasLevelsInSelectedPersonalizedGroup(isFire?: boolean) {
    return this.levels.some((level) =>
      level.isInSelectedPersonalizedGroup(isFire)
    )
  }

  getLevelsPersonalizedGroup(isFire?: boolean) {
    return this.levels.filter((x) => x.isInSelectedPersonalizedGroup(isFire))
  }

  getEquivalentLevel(level: Level) {
    return this.levels.find((x) => x.transversalId === level.transversalId)
  }
}
